import { HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { HttpCacheStore } from './http-cache.store';
import { AuthService } from '../services/auth.service';

export const CACHE_RESET_HEADER = 'x-reset-cache';
export const CACHE_SKIP_HEADER = 'x-skip-cache';

/**
 * GET cache ki common logic. App mein do cache interceptors hain (shared + activity)
 * aur dono ki apni ALAG Map honi chahiye (dekhein HttpCacheStore), is liye base class
 * sirf logic deta hai aur Map har subclass apni banata hai. Pehle dono files ka code
 * copy-paste tha aur ek jagah ka fix doosri jagah reh jata tha.
 */
export abstract class BaseHttpCacheInterceptor implements HttpInterceptor {
  protected readonly cachedData: Map<string, any>;
  /**
   * Har cache reset par barhta hai. Jo request reset se pehle chali thi, uska response
   * apni generation check karta hai — purani generation ka response cleared cache mein
   * wapas nahi jaata (warna login ke waqt in-flight anonymous response cache ko dobara
   * zinda kar deta tha).
   */
  private generation = 0;

  constructor(cacheStore: HttpCacheStore, protected authService: AuthService) {
    this.cachedData = cacheStore.createCache();
  }

  public intercept(httpRequest: HttpRequest<any>, handler: HttpHandler) {

    if (httpRequest.headers.has(CACHE_RESET_HEADER) || ['POST', 'PUT', 'DELETE'].includes(httpRequest.method)) {
      this.resetCache();
    }

    if (httpRequest.method !== 'GET' || httpRequest.headers.has(CACHE_SKIP_HEADER)) {
      return handler.handle(httpRequest);
    }

    const cacheKey = this.buildCacheKey(httpRequest);
    const lastResponse = this.cachedData.get(cacheKey);
    if (lastResponse) {
      return (lastResponse instanceof Observable)
        ? lastResponse : of(lastResponse.clone());
    }

    const generation = this.generation;
    const requestHandle = handler.handle(httpRequest).pipe(
      tap((stateEvent) => {
        if (stateEvent instanceof HttpResponse && generation === this.generation) {
          this.cachedData.set(
            cacheKey,
            stateEvent.clone()
          );
        }
      }),
      share()
    );

    this.cachedData.set(cacheKey, requestHandle);

    return requestHandle;
  }

  /** Subclass yahan apna extra cleanup kar sakta hai (misal: window par rakha SSR data). */
  protected onCacheReset(): void { }

  /**
   * Cache key viewer identity se namespaced hai. Aik hi URL (misal ke taur par
   * /api/user/{groupId}) anonymous visitor aur logged in user ko mukhtalif response
   * deta hai — GroupMembershipStatus, IsAdmin, GroupAdminRoles. Sirf URL par key karne
   * se login se pehle wala anonymous response login ke baad replay ho jata tha, aur
   * admin ko "Join a Group" screen dikhti thi jab tak page reload na ho.
   */
  private buildCacheKey(httpRequest: HttpRequest<any>): string {
    const identity = this.authService.isLoggedIn()
      ? (this.authService.getCurrentUserId() || 'user')
      : 'anonymous';
    return `${identity}|${httpRequest.urlWithParams}`;
  }

  private resetCache(): void {
    this.generation += 1;
    this.cachedData.clear();
    this.onCacheReset();
  }
}
